import { Link } from "react-router-dom";
import { ArrowRight, Check, Clock3, MapPin, Phone, Sparkles } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { BookButton, WhatsAppButton, CallButton, DirectionsButton } from "../components/CTAButtons";
import { ZORBA, SERVICES, SIGNATURE_PACKAGE, ALL_SERVICES } from "../data/content";
import { breadcrumbSchema, daySpaSchema, multiGraph } from "../components/Schema";
import ReviewsSection from "../components/ReviewsSection";
import OfferBanner from "../components/OfferBanner";

function Fact({ label, value, icon: Icon, testid }) {
  const shown = value || "To be confirmed by our partner spa";
  const empty = !value;
  return (
    <div className="fact-row" data-testid={testid || `zorba-fact-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon size={18} />
      <div>
        <strong>{label}</strong>
        <span className={empty ? "muted" : ""}>{shown}</span>
      </div>
    </div>
  );
}

export default function ZorbaProfile({ onBook }) {
  const crumbs = [{ label: "Home", href: "/" }, { label: ZORBA.name }];
  const featured = [SIGNATURE_PACKAGE, ...SERVICES.slice(0, 3)];

  return (
    <>
      <Seo
        title={`${ZORBA.name} — Chandrasekharpur, Bhubaneswar`}
        description={`${ZORBA.name}: a luxury wellness destination in Chandrasekharpur, Bhubaneswar. 18 therapies, published prices from ₹2,500. ${ZORBA.phoneDisplay}.`}
        path="/zorba-international-spa/"
        image={ZORBA.heroImage}
        jsonLd={multiGraph(daySpaSchema(), breadcrumbSchema(crumbs))}
      />
      <section
        className="detail-hero"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(15,23,42,.78), rgba(15,23,42,.3)), url(${ZORBA.heroImage})`,
        }}
      >
        <div className="detail-hero-inner">
          <Breadcrumbs items={crumbs} />
          <p className="hero-label"><span className="pulse-dot" /> {ZORBA.disclosure}</p>
          <h1>{ZORBA.name}<br /><em>{ZORBA.area}, Bhubaneswar.</em></h1>
          <p className="hero-copy">
            <em>"{ZORBA.tagline}."</em> A luxury wellness destination with 18 verified therapies — including the
            signature 6-in-1 experience, four hands massage, Thai, Swedish, Balinese, candle, hot stone, jacuzzi
            and a couple's suite.
          </p>
          <div className="hero-actions">
            <BookButton onBook={onBook} service={`${ZORBA.name} enquiry`} label="Start an enquiry" testid="zorba-book-cta" />
            <CallButton className="btn btn-light" source="zorba_hero" testid="zorba-call-cta" />
            <WhatsAppButton className="btn btn-outline-light" label="WhatsApp" source="zorba_profile" testid="zorba-whatsapp-cta" />
          </div>
        </div>
      </section>

      <section className="section-pad zorba-overview">
        <div className="zorba-overview-grid">
          <article>
            <p className="eyebrow">About</p>
            <h2>A luxury wellness destination in {ZORBA.area}.</h2>
            <p className="large-copy">
              {ZORBA.name} is our featured spa partner in Bhubaneswar. Based at Stalwart Xanadu on
              Chandrasekharpur, the spa offers an 18-therapy menu that spans classic massage styles, traditional
              techniques, warm-water hydrotherapy and the signature 6-in-1 wellness experience — all presented
              in a calm, candle-lit setting.
            </p>
            <h3>Facilities</h3>
            <ul className="check-list two">
              {ZORBA.facilities.map((f) => <li key={f}><Check size={16} />{f}</li>)}
            </ul>
            <h3>Services on offer</h3>
            <div className="pill-row">
              {ZORBA.services.map((s) => <span key={s} className="pill">{s}</span>)}
            </div>
            <div className="actions-row">
              <Link to="/zorba-services/" className="btn btn-secondary" data-testid="zorba-services-link">View service details</Link>
              <Link to="/zorba-packages/" className="btn btn-secondary" data-testid="zorba-packages-link">Signature package</Link>
              <Link to="/zorba-location/" className="btn btn-secondary" data-testid="zorba-location-link">Location & map</Link>
              <Link to="/zorba-contact/" className="btn btn-secondary" data-testid="zorba-contact-link">Contact</Link>
            </div>
          </article>
          <aside className="info-box sticky">
            <Fact label="Address" value={ZORBA.address} icon={MapPin} testid="zorba-fact-address" />
            <Fact label="Phone" value={ZORBA.phoneDisplay} icon={Phone} testid="zorba-fact-phone" />
            <Fact label="Opening hours" value={ZORBA.hours} icon={Clock3} testid="zorba-fact-hours" />
            <Fact label="Price range" value="₹2,500 — ₹12,500" icon={Sparkles} testid="zorba-fact-price-range" />
            <div className="aside-cta-stack">
              <WhatsAppButton label="WhatsApp Zorba" source="zorba_aside" testid="zorba-aside-whatsapp" />
              <CallButton source="zorba_aside" testid="zorba-aside-call" />
              <DirectionsButton testid="zorba-aside-directions" />
              <BookButton onBook={onBook} service={`${ZORBA.name} enquiry`} label="Enquiry form" testid="zorba-aside-book" />
            </div>
            <p className="muted-small">
              Any booking will be confirmed by {ZORBA.name} directly. We don't claim real-time availability.
            </p>
          </aside>
        </div>
      </section>

      <section className="section-pad service-section">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Featured therapies</p>
            <h2>Highlights from the menu.</h2>
          </div>
          <Link to="/zorba-services/" className="text-link" data-testid="zorba-view-all-services">
            View all 18 therapies <ArrowRight size={16} />
          </Link>
        </div>
        <div className="service-grid">
          {featured.map((s, i) => (
            <Link to={`/services/${s.slug}/`} className="service-card" key={s.slug} data-testid={`zorba-service-${i}`}>
              <div className="service-photo" style={{ backgroundImage: `url(${s.image})` }} />
              <div className="service-card-copy">
                <span className="card-kicker">{s.kicker}</span>
                <h3>{s.name}</h3>
                <p>{s.short}</p>
                <div className="service-price-row">
                  <span>{s.priceOptions[0].duration} · {s.priceOptions[0].display}</span>
                  <ArrowRight size={18} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="section-pad price-list-section">
        <h2>Full price list</h2>
        <p className="muted-small">Verified from the official Zorba International Spa menu.</p>
        <div className="price-list-grid">
          <table className="price-table full" data-testid="zorba-full-price-table">
            <thead>
              <tr>
                <th>Service</th>
                <th>60 min</th>
                <th>90 min</th>
                <th>120 min</th>
              </tr>
            </thead>
            <tbody>
              {ALL_SERVICES.map((s) => {
                const d60 = s.priceOptions.find((p) => p.duration === "60 min");
                const d90 = s.priceOptions.find((p) => p.duration === "90 min");
                const d120 = s.priceOptions.find((p) => p.duration === "120 min");
                return (
                  <tr key={s.slug}>
                    <td>
                      <Link to={`/services/${s.slug}/`} data-testid={`price-row-${s.slug}`}>{s.name}</Link>
                    </td>
                    <td>{d60 ? d60.display : "—"}</td>
                    <td>{d90 ? d90.display : "—"}</td>
                    <td>{d120 ? d120.display : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      <OfferBanner variant="card" onBook={onBook} />
      <ReviewsSection />
    </>
  );
}
